import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Languages, Lock, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE } from '../config/api';
const API = `${API_BASE}`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (password.length > 64) {
      toast.error('Password must be at most 64 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Failed to reset password';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0].msg?.replace('Value error, ', '') || message;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 noise-overlay">
        <Card className="bg-background-paper border-border max-w-md" data-testid="reset-password-invalid-card">
          <CardContent className="pt-6 text-center space-y-4">
            <h2 className="font-heading text-2xl font-semibold text-foreground">Invalid Reset Link</h2>
            <p className="text-foreground-muted">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-primary hover:bg-primary/90" data-testid="request-new-link-btn">
                Request New Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 noise-overlay">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center">
              <Languages className="w-6 h-6 text-primary" />
            </div>
            <span className="font-heading text-2xl font-semibold text-foreground">
              <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
            </span>
          </Link>

          <Card className="bg-background-paper border-border" data-testid="reset-password-success-card">
            <CardContent className="pt-6 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-quaternary/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-quaternary" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-foreground">Password Reset!</h2>
              <p className="text-foreground-muted">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <Button 
                className="bg-primary hover:bg-primary/90"
                onClick={() => navigate('/login')}
                data-testid="go-to-login-btn"
              >
                Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 noise-overlay">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="reset-password-logo">
          <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center">
            <Languages className="w-6 h-6 text-primary" />
          </div>
          <span className="font-heading text-2xl font-semibold text-foreground">
            <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
          </span>
        </Link>

        <Card className="bg-background-paper border-border" data-testid="reset-password-card">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-2xl text-foreground">Reset Password</CardTitle>
            <CardDescription className="text-foreground-muted">
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-background border-border focus:ring-primary"
                    required
                    maxLength={64}
                    autoComplete="new-password"
                    data-testid="reset-password-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-foreground">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-background border-border focus:ring-primary"
                    required
                    maxLength={64}
                    autoComplete="new-password"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="reset-password-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
